import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      try {
        const parsed = JSON.parse(userData);
        setUser(parsed);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        // F4 Step 6 (1 Nov 2026): a session cached before this change has no
        // is_admin/is_super_admin/is_pm_role — refresh once from /auth/me so an
        // already-logged-in admin doesn't silently lose admin UI after a deploy.
        api.get('/auth/me').then((res) => {
          localStorage.setItem('user', JSON.stringify(res.data));
          setUser(res.data);
        }).catch(() => {});
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      const next = { ...prev, ...patch };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  }, []);

  // ==================== NEW PERMISSION SYSTEM ====================

  // Check if user can access a module with a specific action
  // Uses module_permissions from the new Profile system
  const canAccess = useCallback((moduleId, action = 'VIEW') => {
    if (!user) return false;

    // New system: module_permissions = { MODULE_ID: ["VIEW","CREATE",...] }
    const modulePerms = user.module_permissions;
    if (modulePerms && Object.keys(modulePerms).length > 0) {
      const actions = modulePerms[moduleId] || [];
      return actions.includes(action);
    }

    // Legacy fallback: use old permissions object
    return true; // Allow all if no module_permissions set yet
  }, [user]);

  // Get data scope for the current user
  const dataScope = user?.data_scope || 'ALL';

  // Legacy hasPermission for backward compatibility
  const hasPermission = useCallback((permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions[permission] === true;
  }, [user]);

  const hasRole = useCallback((role) => {
    if (!user || !user.roles) return false;
    return user.roles.includes(role);
  }, [user]);

  // F4 Step 6 (1 Nov 2026): profile-based booleans from the backend's UserResponse,
  // replacing hasRole('super_admin')/hasRole('admin')/hasRole('product_admin') combos.
  const isAdmin = !!user?.is_admin;
  const isSuperAdmin = !!user?.is_super_admin;
  const isPmRole = !!user?.is_pm_role;

  const value = {
    user,
    loading,
    login,
    logout,
    updateUser,
    canAccess,
    dataScope,
    hasPermission,
    hasRole,
    isAdmin,
    isSuperAdmin,
    isPmRole,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
